import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, AlertCircle, Clock, DollarSign, TrendingUp, User, Calendar, MessageSquare, Filter, ChevronDown, ChevronUp, ExternalLink, BarChart3, CreditCard, Layers, Download, Loader2, ShieldAlert, History, RotateCcw, Percent, Trash2, Lock, FileText, X, PieChart, Activity } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import PdfViewerModal from '@/components/PdfViewerModal';
import BulkActionBar from '@/components/BulkActionBar';
import AuditTrailModal from '@/components/AuditTrailModal';
import ProfitabilityTablesDetailed from '@/components/ProfitabilityTablesDetailed';
import { useApprovalData } from '@/hooks/useApprovalData';
import { useApprovalActions } from '@/hooks/useApprovalActions';


export default function Approvals() {
  const navigate = useNavigate();
  
  // Use Approval Data Hook
  const {
    loading,
    proposals,
    filter,
    setFilter,
    sortBy,
    setSortBy,
    searchTerm,
    setSearchTerm,
    filteredProposals,
    statusCounts,
    fetchProposals
  } = useApprovalData();
  
  // Use Approval Actions Hook
  const {
    processing,
    setProcessing,
    showApprovalModal,
    setShowApprovalModal,
    selectedProposal,
    setSelectedProposal,
    approvalAction,
    setApprovalAction,
    comments,
    setComments,
    handleApprove,
    handleReject,
    confirmApproval,
    handleReopen,
    showReopenConfirm,
    confirmReopen,
    cancelReopen
  } = useApprovalActions(fetchProposals);
  
  const [user, setUser] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: null, blob: null, filename: '' });
  const [selectedProposals, setSelectedProposals] = useState(new Set());
  const [auditTrailModal, setAuditTrailModal] = useState({ show: false, collectionId: null, collectionName: '' });
  const [kpiModal, setKpiModal] = useState({ show: false, proposal: null });

  // Handler for double-click KPI summary modal
  const handleDoubleClickKpi = (proposal) => {
    setKpiModal({ show: true, proposal });
  };

  const handleExportPdf = async (proposal, e) => {
    e.stopPropagation();
    try {
      setExportingId(proposal.collection_id);
      const res = await api.post('/scenarios/approval-export-pdf', proposal, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const filename = `GW_Approval_${(proposal.collection_name || 'export').slice(0, 30)}.pdf`;
      setPdfViewer({ show: true, url: blobUrl, blob, filename });
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setExportingId(null);
    }
  };

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: null, blob: null, filename: '' });
  };

  const handleDeleteClick = (proposal, e) => {
    e.stopPropagation();
    setProposalToDelete(proposal);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!proposalToDelete) return;
    
    const collectionId = proposalToDelete.collection_id;
    
    try {
      setDeletingId(collectionId);
      
      // Delete from backend
      await api.delete(`/collections/${collectionId}`);
      
      // Close modal immediately
      setShowDeleteModal(false);
      setProposalToDelete(null);
      
      // Show success toast
      toast.success('Approval deleted successfully');
      
      // Refresh proposals list to get updated data
      await fetchProposals();
      
    } catch (err) {
      console.error('Delete error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to delete approval';
      toast.error(errorMsg);
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProposalToDelete(null);
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  // Wrapper for single or bulk approval submission
  const submitApproval = async () => {
    if (!selectedProposal && selectedProposals.size === 0) return;
    
    // If bulk action (multiple proposals selected)
    if (selectedProposals.size > 0) {
      await submitBulkAction();
      return;
    }
    
    // Single proposal action (uses hook) - hook manages its own state
    await confirmApproval();
  };

  // Bulk action handlers
  const toggleProposalSelection = (proposalId) => {
    setSelectedProposals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(proposalId)) {
        newSet.delete(proposalId);
      } else {
        newSet.add(proposalId);
      }
      return newSet;
    });
  };

  const handleBulkApprove = () => {
    setApprovalAction('approved');
    setComments('');
    setShowApprovalModal(true);
  };

  const handleBulkReject = () => {
    setApprovalAction('rejected');
    setComments('');
    setShowApprovalModal(true);
  };

  const submitBulkAction = async () => {
    if (selectedProposals.size === 0) return;
    
    try {
      setProcessing(true);
      
      // Collect all scenario IDs from selected proposals
      const selectedProposalsList = proposals.filter(p => selectedProposals.has(p.collection_id));
      const scenarioIds = selectedProposalsList.flatMap(p => 
        p.services
          .filter(s => ['pending_approval', 'pending_cfo_approval', 'pending_ceo_approval'].includes(s.status))
          .map(s => s.scenario_id || s.id)  // Use scenario_id (preferred) or fallback to id
      );

      if (scenarioIds.length === 0) {
        toast.error('No pending scenarios found in selected proposals');
        return;
      }

      // Call bulk action endpoint
      const endpoint = approvalAction === 'approved' ? '/bulk/approve' : '/bulk/reject';
      const response = await api.post(endpoint, {
        scenario_ids: scenarioIds,
        comments: comments || `Bulk ${approvalAction}`
      });

      const result = response.data;
      const actionCount = approvalAction === 'approved' ? result.approved_count : result.rejected_count;
      
      toast.success(`${actionCount} scenario${actionCount !== 1 ? 's' : ''} ${approvalAction} successfully`);
      
      if (result.failed_count > 0) {
        toast.warning(`${result.failed_count} scenario${result.failed_count !== 1 ? 's' : ''} could not be processed`);
      }

      setShowApprovalModal(false);
      setComments('');
      setSelectedProposals(new Set());
      await fetchProposals();
    } catch (error) {
      console.error('Error in bulk action:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk action');
    } finally {
      setProcessing(false);
    }
  };

  const clearSelection = () => {
    setSelectedProposals(new Set());
  };

  // Bulk Export Handler
  const handleBulkExport = async () => {
    if (selectedProposals.size === 0) return;

    try {
      const selectedList = proposals.filter(p => selectedProposals.has(p.collection_id));
      
      toast.info(`Exporting ${selectedList.length} proposal(s)...`);

      // Export each proposal as PDF
      for (const proposal of selectedList) {
        try {
          const response = await api.get(`/collections/${proposal.collection_id}/export-pdf`, {
            responseType: 'blob'
          });

          // Create download link
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${proposal.name.replace(/[^a-z0-9]/gi, '_')}_${proposal.collection_id.slice(0, 8)}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (err) {
          console.error(`Error exporting ${proposal.name}:`, err);
          toast.error(`Failed to export: ${proposal.name}`);
        }
      }

      toast.success(`Successfully exported ${selectedList.length} proposal(s)`);
      setSelectedProposals(new Set());
    } catch (error) {
      console.error('Bulk export error:', error);
      toast.error('Failed to complete bulk export');
    }
  };

  const getApprovalLevelBadge = (proposal) => {
    const level = proposal.approval_level;
    if (level === 'cfo_ceo') return (
      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border bg-red-100 text-red-700 border-red-300">
        <AlertTriangle className="w-3 h-3" /><span className="font-semibold">CFO/CEO Approval Required</span>
      </span>
    );
    if (level === 'manager') return (
      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border bg-orange-100 text-orange-700 border-orange-300">
        <AlertCircle className="w-3 h-3" /><span className="font-semibold">Manager Approval Required</span>
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border bg-blue-100 text-blue-700 border-blue-300">
        <CheckCircle className="w-3 h-3" /><span className="font-semibold">Standard Approval</span>
      </span>
    );
  };

  const getApprovalStageBadge = (status) => {
    // Map backend status to user-friendly stage names
    const stageMap = {
      'pending_admin_approval': { text: 'Admin Review', color: 'purple', icon: Clock },
      'pending_manager_approval': { text: 'Manager Review', color: 'amber', icon: Clock },
      'pending_cfo_approval': { text: 'CFO Review', color: 'orange', icon: Clock },
      'pending_ceo_approval': { text: 'CEO Review', color: 'red', icon: Clock },
      'pending_approval': { text: 'Pending Review', color: 'blue', icon: Clock },
      'approved': { text: 'Approved', color: 'green', icon: CheckCircle },
      'rejected': { text: 'Rejected', color: 'red', icon: XCircle }
    };

    const stage = stageMap[status] || { text: 'Pending', color: 'slate', icon: Clock };
    const Icon = stage.icon;

    return (
      <span className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border bg-${stage.color}-50 text-${stage.color}-700 border-${stage.color}-200`}>
        <Icon className="w-3 h-3" />
        <span className="font-semibold">{stage.text}</span>
      </span>
    );
  };

  const fc = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fp = (v) => `${(v || 0).toFixed(2)}%`;

  // Use statusCounts from the hook (already calculated)
  const sortedProposals = filteredProposals; // Hook already handles sorting

  if (loading && proposals.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-3 text-sm text-slate-500">Loading approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="approvals-page" className="p-6 lg:p-8 space-y-8 max-w-[1400px] animate-fade-in">
      {/* Glass Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-heading-tight">Approvals</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">
              <span className="text-primary font-semibold">{proposals.length}</span> proposal{proposals.length !== 1 ? 's' : ''} awaiting your review
            </p>
          </div>
        </div>
      </div>
      
      {/* Approval Workflow Guide */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
        <div className="flex gap-3">
          <ShieldAlert className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-bold text-purple-900 mb-2">🛡️ Approval Workflow Guide</h3>
            <div className="text-xs text-purple-800 space-y-1.5">
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[140px]">📋 Review Proposal:</span>
                <span>Check financial metrics, approval reasons, and service details carefully</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[140px]">💬 Add Comments:</span>
                <span>Provide feedback or ask questions before making your decision</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[140px]">✅ Approve/Reject:</span>
                <span>Make your decision - some proposals require multiple approval levels</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[140px]">🔄 Bulk Actions:</span>
                <span>Select multiple proposals to approve or reject in one action</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold min-w-[140px]">📊 Export PDF:</span>
                <span>Download detailed proposal reports for offline review and records</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Enhanced */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
          <Filter className="w-4 h-4" />
          <span>Filter:</span>
        </div>
        {[
          { key: 'all', label: 'All', count: proposals.length, color: 'slate' },
          { key: 'critical', label: 'CFO/CEO', count: statusCounts.critical, color: 'red' },
          { key: 'high', label: 'Manager', count: statusCounts.high, color: 'amber' },
          { key: 'standard', label: 'Standard', count: statusCounts.standard, color: 'blue' },
        ].map(f => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              filter === f.key 
                ? `bg-${f.color === 'slate' ? 'primary' : f.color}-100 text-${f.color === 'slate' ? 'primary' : f.color}-700 shadow-md` 
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
            }`}
          >
            {f.label} <span className="ml-1.5 opacity-75">({f.count})</span>
          </button>
        ))}
        <select
          data-testid="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="ml-auto text-sm border border-slate-200 rounded-xl px-4 py-2 bg-white hover:border-primary transition-colors font-medium"
        >
          <option value="oldest">Oldest First</option>
          <option value="newest">Newest First</option>
          <option value="highest_revenue">Highest Revenue</option>
          <option value="lowest_revenue">Lowest Revenue</option>
        </select>
      </div>

      {/* Proposal Cards */}
      {sortedProposals.length === 0 ? (
        <div className="text-center py-20 card-modern">
          <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">All caught up!</h3>
          <p className="text-sm text-slate-500 mt-2">No pending approvals in this category.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {sortedProposals.map((proposal) => {
            const agg = proposal.aggregated_totals || {};
            const isExpanded = expandedId === proposal.collection_id;
            const info = proposal.scenario_info || {};

            return (
              <div
                key={proposal.collection_id}
                data-testid={`approval-card-${proposal.collection_id}`}
                className={`card-modern transition-all duration-300 ${isExpanded ? 'ring-2 ring-primary/30 shadow-xl' : 'hover:shadow-lg'}`}
              >
                {/* Card Header */}
                <div
                  className="p-6 cursor-pointer select-none"
                  onDoubleClick={() => handleDoubleClickKpi(proposal)}
                >
                  {/* Proposal Title with Checkbox */}
                  <div className="flex items-start gap-3 mb-1">
                    {/* Selection Checkbox - Manager+ only */}
                    {['admin', 'manager', 'approver', 'cfo', 'ceo'].includes(user?.role) && (
                      <input
                        type="checkbox"
                        checked={selectedProposals.has(proposal.collection_id)}
                        onChange={() => toggleProposalSelection(proposal.collection_id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        title="Select for bulk action (Manager+ only)"
                      />
                    )}
                    <div className="flex-1">
                      <p data-testid={`proposal-title-${proposal.collection_id}`} className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                        {proposal.collection_name || 'Untitled Proposal'}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                          <Layers className="w-4 h-4 text-slate-400" />
                          {proposal.service_count} Service{proposal.service_count !== 1 ? 's' : ''}
                        </h3>
                        {getApprovalStageBadge(proposal.status)}
                        {getApprovalLevelBadge(proposal)}
                        <button
                          data-testid={`expand-btn-${proposal.collection_id}`}
                          onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : proposal.collection_id); }}
                          className="ml-auto p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title={isExpanded ? 'Collapse' : 'Expand (or double-click)'}
                        >
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                        {proposal.services.map((svc, i) => (
                          <span key={svc.id}>
                            <span className="font-medium">{svc.service_name || 'Service'}</span>
                            {i < proposal.services.length - 1 && <span className="text-slate-300 mx-1">|</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {proposal.can_current_user_approve !== false ? (
                        <>
                          <button
                            data-testid={`view-proposal-btn-${proposal.collection_id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/proposal/${proposal.collection_id}`); }}
                            className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
                            title="View full proposal"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setAuditTrailModal({ 
                                show: true, 
                                collectionId: proposal.collection_id, 
                                collectionName: proposal.collection_name || 'Proposal' 
                              }); 
                            }}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
                            title="View audit trail"
                          >
                            <History className="w-4 h-4" />
                            History
                          </button>
                          <button
                            data-testid={`export-pdf-btn-${proposal.collection_id}`}
                            onClick={(e) => handleExportPdf(proposal, e)}
                            disabled={exportingId === proposal.collection_id}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200 disabled:opacity-50"
                          >
                            {exportingId === proposal.collection_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            PDF
                          </button>
                          
                          {/* Delete Button - Admin Only */}
                          {user?.role === 'admin' && (
                            <button
                              data-testid={`delete-btn-${proposal.collection_id}`}
                              onClick={(e) => handleDeleteClick(proposal, e)}
                              disabled={deletingId === proposal.collection_id}
                              className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors border border-red-200 disabled:opacity-50"
                              title="Delete this approval (Admin only)"
                            >
                              {deletingId === proposal.collection_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              Delete
                            </button>
                          )}
                          
                          {/* Check if all services are approved or rejected (completed statuses) */}
                          {proposal.services.every(s => ['approved', 'rejected'].includes(s.status)) ? (
                            // Show Reopen button for completed proposals
                            <>
                              {(proposal.approval_status === 'approved' || proposal.approval_status === 'fully_approved') && (
                                <button
                                  data-testid={`generate-agreement-btn-${proposal.collection_id}`}
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    navigate(`/agreement/${proposal.collection_id}`); 
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
                                  title="Generate SaaS Agreement (Available to all users)"
                                >
                                  <FileText className="w-4 h-4" />
                                  Generate Agreement
                                </button>
                              )}
                              {/* Reopen button - Manager+ only */}
                              {['admin', 'manager', 'approver', 'cfo', 'ceo'].includes(user?.role) && (
                                <>
                                  <button
                                    data-testid={`reopen-btn-${proposal.collection_id}`}
                                    onClick={(e) => { e.stopPropagation(); handleReopen(proposal); }}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50"
                                    title="Reopen proposal (Manager+ only)"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                    Reopen
                                  </button>
                                  {/* Inline confirmation — replaces window.confirm (blocked in most envs) */}
                                  {showReopenConfirm === proposal.collection_id && (
                                    <div className="flex items-center space-x-2 bg-blue-50 border border-blue-300 rounded-lg px-3 py-2">
                                      <span className="text-xs text-blue-900 font-medium">Reopen "{proposal.collection_name}"?</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); confirmReopen(proposal); }}
                                        className="px-2 py-1 text-white bg-blue-600 hover:bg-blue-700 rounded text-xs font-bold"
                                        data-testid={`confirm-reopen-${proposal.collection_id}`}
                                      >
                                        Yes
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); cancelReopen(); }}
                                        className="px-2 py-1 text-slate-600 hover:bg-slate-100 rounded text-xs"
                                      >
                                        No
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            // Show Approve/Reject buttons for pending proposals - Manager+ only
                            <>
                              {['admin', 'manager', 'approver', 'cfo', 'ceo'].includes(user?.role) && (
                                <>
                                  <button
                                    data-testid={`reject-btn-${proposal.collection_id}`}
                                    onClick={(e) => { e.stopPropagation(); handleReject(proposal); }}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors border border-red-200 disabled:opacity-50"
                                    title="Reject proposal (Manager+ only)"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Reject
                                  </button>
                                  <button
                                    data-testid={`approve-btn-${proposal.collection_id}`}
                                    onClick={(e) => { e.stopPropagation(); handleApprove(proposal); }}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors border border-emerald-200 disabled:opacity-50"
                                    title="Approve proposal (Manager+ only)"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Approve All
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium border border-slate-200">
                          <Clock className="w-4 h-4" />
                          Awaiting Higher Approval
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Aggregated Summary Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-slate-600" />
                        <span className="text-sm text-slate-600">Annual Gross Revenue</span>
                      </div>
                      <p className="text-xl font-bold text-slate-900">{fc(agg.gross_revenue)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm text-slate-600">Annual Net Revenue</span>
                      </div>
                      <p className="text-xl font-bold text-emerald-600">{fc(agg.net_fees)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-600">Gross Profit Margin</span>
                      </div>
                      <p className="text-xl font-bold text-blue-600">{fp(agg.gpm)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Percent className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-slate-600">Net Revenue %</span>
                      </div>
                      <p className="text-xl font-bold text-purple-600">{fp(agg.npm)}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Net Profit Margin</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Layers className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm text-slate-600">Services</span>
                      </div>
                      <p className="text-xl font-bold text-indigo-600">{proposal.service_count}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{(agg.total_transactions || 0).toLocaleString()} txns/yr</p>
                    </div>
                  </div>

                  {/* Approval Reasons */}
                  {proposal.approval_reasons && proposal.approval_reasons.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-amber-900 mb-2 text-sm">Approval Required Because:</h4>
                      <ul className="space-y-1">
                        {proposal.approval_reasons.map((reason, idx) => (
                          <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                            <span className="text-amber-600 mt-1">&bull;</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Service Rates Snapshot — quick view of proposed rates per service (no expand needed) */}
                  {proposal.services?.length > 0 && (
                    <div data-testid={`rates-snapshot-${proposal.collection_id}`} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-slate-900 mb-2 text-sm flex items-center gap-2">
                        <span className="text-indigo-700">Service Rates Snapshot</span>
                        <span className="text-xs font-normal text-slate-500">(quick view — expand for full breakdown)</span>
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-600">
                              <th className="text-left py-1.5 px-2 font-semibold">Service</th>
                              <th className="text-center py-1.5 px-2 font-semibold">CC Rate</th>
                              <th className="text-center py-1.5 px-2 font-semibold">CC Min</th>
                              <th className="text-center py-1.5 px-2 font-semibold">AmEx Rate</th>
                              <th className="text-center py-1.5 px-2 font-semibold">eCheck</th>
                              <th className="text-center py-1.5 px-2 font-semibold">Readers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {proposal.services.map((svc, i) => {
                              const pp = svc.inputs?.proposed_pricing?.paymentRates || svc.proposed_pricing?.paymentRates || {};
                              const ccRate = pp.CC?.merchantFee;
                              const ccMin = pp.CC?.minFee;
                              const amexRate = pp.AMEX?.merchantFee;
                              const ach = pp.ACH?.minFee;
                              const sInfo = svc.scenario_info || {};
                              const rqty = sInfo.readersQty || 0;
                              const rtype = sInfo.readerType || '';
                              const fmtRate = (v) => v != null ? `${Number(v).toFixed(2)}%` : '—';
                              const fmtMoney = (v) => v != null ? `$${Number(v).toFixed(2)}` : '—';
                              return (
                                <tr key={svc.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="py-1.5 px-2">
                                    <span className="font-medium text-slate-800">{svc.service_name || 'Service'}</span>
                                    {svc.merchant_fees_customized && (
                                      <span className="ml-1 inline-flex items-center px-1 py-0.5 bg-orange-100 text-orange-700 rounded text-[9px] font-semibold">CUSTOM</span>
                                    )}
                                  </td>
                                  <td className="text-center py-1.5 px-2 font-mono text-slate-700">{fmtRate(ccRate)}</td>
                                  <td className="text-center py-1.5 px-2 font-mono text-slate-700">{fmtMoney(ccMin)}</td>
                                  <td className="text-center py-1.5 px-2 font-mono text-slate-700">{fmtRate(amexRate)}</td>
                                  <td className="text-center py-1.5 px-2 font-mono text-slate-700">{fmtMoney(ach)}</td>
                                  <td className="text-center py-1.5 px-2 text-slate-700">
                                    {rqty > 0 && rtype && rtype !== 'N/A' ? `${rqty} × ${rtype}` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Custom Merchant Fee Warning */}
                  {proposal.services?.some(s => s.merchant_fees_customized) && (
                    <div data-testid={`custom-fee-warning-${proposal.collection_id}`} className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 mb-4">
                      <ShieldAlert className="w-4.5 h-4.5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-orange-800">Merchant Fees Modified</p>
                        <p className="text-xs text-orange-600 mt-0.5">
                          One or more services have merchant fees that differ from the standard service rates. Review the per-service breakdown below for details.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-sm text-slate-600 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>Submitted by <span className="font-medium text-slate-800">{proposal.submitter_name || proposal.creator_name || info.accountExecutive || 'Unknown'}</span></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(proposal.submitted_at || Date.now()).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {!isExpanded && (
                      <span className="text-xs text-slate-400 italic">Double-click for KPI summary</span>
                    )}
                  </div>
                </div>

                {/* Expanded Detail — Per-Service Breakdown */}
                {isExpanded && (
                  <div data-testid={`detail-panel-${proposal.collection_id}`} className="border-t border-slate-200 bg-slate-50/50 px-6 py-5 space-y-5">
                    {/* View Full Proposal link */}
                    <div className="flex justify-end">
                      <button
                        data-testid={`view-proposal-btn-${proposal.collection_id}`}
                        onClick={() => navigate(`/proposal/${proposal.collection_id}`)}
                        className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View Full Proposal
                      </button>
                    </div>

                    {/* Per-Service Cards */}
                    {proposal.services.map((svc, svcIdx) => {
                      const svcCalc = svc.calculation_results || {};
                      const svcTotals = svcCalc.totals || {};
                      const svcNetFees = svcCalc.net_fees || {};
                      const svcProf = svc.profitability_results || {};
                      const svcProfTotals = svcProf.totals || {};
                      const svcInfo = svc.scenario_info || {};
                      const svcPaymentTypes = svcProf.payment_types
                        ? (typeof svcProf.payment_types === 'object' && !Array.isArray(svcProf.payment_types)
                          ? Object.keys(svcProf.payment_types).map(k => ({ name: k, ...svcProf.payment_types[k] }))
                          : svcProf.payment_types)
                        : [];
                      // Filter to only payment types with actual data
                      const activePTs = svcPaymentTypes.filter(pt => (pt.total_fees || 0) > 0 || (pt.total_transactions || 0) > 0);
                      const isPending = ['pending_approval', 'pending_cfo_approval', 'pending_ceo_approval'].includes(svc.status);

                      return (
                        <div key={svc.id} data-testid={`service-detail-${svc.id}`} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                          {/* Service Header */}
                          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-white bg-indigo-600 rounded-full w-6 h-6 flex items-center justify-center">{svcIdx + 1}</span>
                              <div>
                                <h4 className="font-semibold text-slate-900 text-sm">{svc.service_name || 'Service'}</h4>
                                <p className="text-xs text-slate-500">{svc.name || ''}{svcInfo.municipality ? ` — ${svcInfo.municipality}${svcInfo.state ? `, ${svcInfo.state}` : ''}` : ''}</p>
                              </div>
                              {svc.merchant_fees_customized && (
                                <span data-testid={`custom-fee-badge-${svc.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[11px] font-semibold border border-orange-200">
                                  <ShieldAlert className="w-3 h-3" />
                                  Custom Fees
                                </span>
                              )}
                            </div>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              svc.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              svc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              isPending ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {svc.status === 'approved' ? 'Approved' :
                               svc.status === 'rejected' ? 'Rejected' :
                               isPending ? 'Pending' :
                               svc.status}
                            </span>
                          </div>

                          {/* Reader Cost Recovery Information */}
                          {svc.reader_cost_recovery?.applied && (
                            <div className="px-5 py-3 bg-green-50 border-b border-green-200">
                              <div className="flex items-start gap-2">
                                <Lock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-green-900 mb-1">
                                    Reader Cost Recovery Applied
                                  </p>
                                  <p className="text-xs text-green-700 mb-2">
                                    Rates adjusted to cover {svc.reader_cost_recovery.reader_quantity} {svc.reader_cost_recovery.reader_type} reader(s)
                                  </p>
                                  <div className="grid grid-cols-3 gap-3 bg-white rounded border border-green-200 p-2">
                                    <div>
                                      <p className="text-xs text-slate-600">Original CC Rate</p>
                                      <p className="text-sm font-semibold text-red-600 line-through">
                                        {svc.reader_cost_recovery.original_rates?.CC?.toFixed(2)}%
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-600">Adjusted CC Rate</p>
                                      <p className="text-sm font-bold text-green-700">
                                        {svc.reader_cost_recovery.adjusted_rates?.CC?.toFixed(2)}%
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-slate-600">Rate Increase</p>
                                      <p className="text-sm font-bold text-green-600">
                                        +{((svc.reader_cost_recovery.adjusted_rates?.CC || 0) - (svc.reader_cost_recovery.original_rates?.CC || 0)).toFixed(2)}%
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Proposed Rates & Minimums */}
                          {(() => {
                            const pRates = (svc.inputs?.proposed_pricing?.paymentRates) || (svc.proposed_pricing?.paymentRates) || {};
                            const entries = Object.entries(pRates).filter(([, v]) => v && (v.takeRate !== undefined || v.minFee !== undefined));
                            if (entries.length === 0) return null;
                            const ptLabel = { CC: 'Credit Card', AMEX: 'AmEx', DEBIT: 'Debit Card', ACH: 'ACH / E-Check' };
                            const ptColor = { CC: 'bg-blue-50 text-blue-700', AMEX: 'bg-purple-50 text-purple-700', DEBIT: 'bg-teal-50 text-teal-700', ACH: 'bg-amber-50 text-amber-700' };
                            return (
                              <div data-testid={`proposed-rates-${svc.id}`} className="px-5 pb-3">
                                <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                  <CreditCard className="w-3.5 h-3.5 text-indigo-500" />
                                  Proposed Rates &amp; Minimums
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {entries.map(([type, vals]) => (
                                    <div key={type} className={`rounded-lg border px-3 py-2.5 ${ptColor[type] || 'bg-slate-50 text-slate-700'} border-current/20`}>
                                      <p className="text-[11px] font-semibold opacity-75 mb-1.5">{ptLabel[type] || type}</p>
                                      <div className="flex justify-between items-baseline gap-1">
                                        <div className="text-center">
                                          <p className="text-[10px] opacity-60 leading-none">Rate</p>
                                          <p className="text-base font-extrabold leading-tight">
                                            {vals.takeRate !== undefined ? `${Number(vals.takeRate).toFixed(2)}%` : '—'}
                                          </p>
                                        </div>
                                        <div className="w-px h-8 bg-current opacity-10" />
                                        <div className="text-center">
                                          <p className="text-[10px] opacity-60 leading-none">Min Fee</p>
                                          <p className="text-base font-extrabold leading-tight">
                                            {vals.minFee !== undefined ? `$${Number(vals.minFee).toFixed(2)}` : '—'}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Service Key Metrics */}
                          <div className="px-5 py-3">
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                              {[
                                { label: 'Annual Txns', value: (svcProf.annual_transactions || 0).toLocaleString() },
                                { label: 'Total Payments', value: fc(svcTotals.total_payments) },
                                { label: 'Gross Revenue', value: fc(svcTotals.total_fees) },
                                { label: 'Merchant Fees', value: fc(svcTotals.merchant_fees), color: 'text-red-600' },
                                { label: 'Gross Profit', value: fc(svcTotals.net_revenue), color: 'text-emerald-700' },
                                { label: 'GPM', value: fp(svcTotals.gpm), color: 'text-blue-700' },
                              ].map((m, i) => (
                                <div key={i} className="text-center">
                                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">{m.label}</p>
                                  <p className={`text-sm font-bold mt-0.5 ${m.color || 'text-slate-900'}`}>{m.value}</p>
                                </div>
                              ))}
                            </div>

                            {/* Payment Type Table */}
                            {activePTs.length > 0 && (
                              <div className="overflow-x-auto rounded-lg border border-slate-200 mb-3">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-slate-100 text-slate-700">
                                      <th className="text-left px-3 py-2 font-semibold">Payment Type</th>
                                      <th className="text-right px-3 py-2 font-semibold">Txns</th>
                                      <th className="text-right px-3 py-2 font-semibold">Payments</th>
                                      <th className="text-right px-3 py-2 font-semibold">Fees</th>
                                      <th className="text-right px-3 py-2 font-semibold">Merchant</th>
                                      <th className="text-right px-3 py-2 font-semibold">Gross Profit</th>
                                      <th className="text-right px-3 py-2 font-semibold">GPM</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {activePTs.map((pt, i) => (
                                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                        <td className="px-3 py-1.5 font-medium text-slate-800">{pt.name || pt.payment_type}</td>
                                        <td className="px-3 py-1.5 text-right text-slate-700">{(pt.total_transactions || 0).toLocaleString()}</td>
                                        <td className="px-3 py-1.5 text-right text-slate-700">{fc(pt.total_payments)}</td>
                                        <td className="px-3 py-1.5 text-right text-slate-700">{fc(pt.total_fees)}</td>
                                        <td className="px-3 py-1.5 text-right text-red-600">{fc(pt.merchant_fees)}</td>
                                        <td className="px-3 py-1.5 text-right text-emerald-600 font-medium">{fc(pt.net_revenue)}</td>
                                        <td className="px-3 py-1.5 text-right text-blue-600 font-medium">{fp(pt.gpm)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Custom Merchant Fee Detail */}
                            {svc.merchant_fees_customized && svc.original_merchant_fees && (
                              <div data-testid={`custom-fee-detail-${svc.id}`} className="bg-orange-50/60 border border-orange-200/80 rounded-lg px-4 py-3 mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ShieldAlert className="w-3.5 h-3.5 text-orange-600" />
                                  <span className="text-xs font-semibold text-orange-800">Merchant Fee Variance</span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {Object.entries(svc.original_merchant_fees || {}).map(([type, rate]) => {
                                    const currentRate = svc.inputs?.proposed_pricing?.paymentRates?.[type]?.merchantFee ??
                                                       svc.proposed_pricing?.paymentRates?.[type]?.merchantFee;
                                    const isDiff = currentRate !== undefined && Math.abs(currentRate - rate) > 0.001;
                                    if (!isDiff) return null;
                                    return (
                                      <div key={type} className="bg-white rounded border border-orange-200/60 px-2.5 py-1.5">
                                        <p className="text-[10px] text-slate-500 uppercase font-medium">{type}</p>
                                        <p className="text-xs text-slate-400 line-through">{rate}%</p>
                                        <p className="text-xs font-bold text-orange-700">{currentRate}%</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Net Revenue Breakdown */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {[
                                { label: 'Implied TR', pct: svcProfTotals.implied_tr },
                                { 
                                  label: 'Partnership', 
                                  value: svcNetFees.partnership_incentive,
                                  percentage: svcProfTotals.total_fees > 0 ? 
                                    ((typeof svcNetFees.partnership_incentive === 'object' ? svcNetFees.partnership_incentive.total_cost : svcNetFees.partnership_incentive) / svcProfTotals.total_fees * 100) : 0
                                },
                                { 
                                  label: 'Sales Comm.', 
                                  value: svcNetFees.sales_commissions,
                                  percentage: svcProfTotals.total_fees > 0 ? 
                                    ((typeof svcNetFees.sales_commissions === 'object' ? svcNetFees.sales_commissions.total_cost : svcNetFees.sales_commissions) / svcProfTotals.total_fees * 100) : 0
                                },
                                { 
                                  label: 'Reader Costs', 
                                  value: svcNetFees.reader_costs,
                                  percentage: svcProfTotals.total_fees > 0 ? 
                                    ((typeof svcNetFees.reader_costs === 'object' ? svcNetFees.reader_costs.total_cost : svcNetFees.reader_costs) / svcProfTotals.total_fees * 100) : 0
                                },
                                { 
                                  label: 'SIM Costs', 
                                  value: svcNetFees.sim_costs,
                                  percentage: svcProfTotals.total_fees > 0 ? 
                                    ((typeof svcNetFees.sim_costs === 'object' ? svcNetFees.sim_costs.total_cost : svcNetFees.sim_costs) / svcProfTotals.total_fees * 100) : 0
                                },
                                { label: 'Net Fees', value: svcNetFees.net_fees, color: 'text-emerald-700 font-bold' },
                                { label: 'Net Margin', pct: svcNetFees.net_fees_margin, color: 'text-blue-700 font-bold' },
                              ].map((item, i) => {
                                const val = item.pct !== undefined ? fp(item.pct) :
                                  fc(typeof item.value === 'object' ? item.value?.total_cost : item.value);
                                return (
                                  <div key={i} className="bg-slate-50 rounded border border-slate-200 px-2.5 py-1.5">
                                    <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                                    <p className={`text-xs font-semibold ${item.color || 'text-slate-700'}`}>{val}</p>
                                    {item.percentage !== undefined && item.percentage > 0 && (
                                      <p className="text-[9px] text-slate-500">{fp(item.percentage)} of Gross</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Per-Service Profitability Tables */}
                            {svcProf && svcProf.payment_types && (
                              <div className="mt-4 pt-4 border-t border-slate-200">
                                <h5 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                                  <CreditCard className="w-4 h-4" />
                                  Payment Type Profitability Breakdown
                                </h5>
                                <ProfitabilityTablesDetailed profitabilityResult={svcProf} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Combined Profitability Tables for All Services */}
                    {proposal.services.length > 1 && proposal.aggregated_profitability && (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200 p-5 mb-4">
                        <h4 className="font-semibold text-indigo-900 mb-3 text-base flex items-center gap-2">
                          <BarChart3 className="w-5 h-5" />
                          Combined Profitability (All Services)
                        </h4>
                        <ProfitabilityTablesDetailed profitabilityResult={proposal.aggregated_profitability} />
                      </div>
                    )}

                    {/* Aggregated Proposal Totals */}
                    <div className="bg-indigo-50 rounded-lg border border-indigo-200 p-4">
                      <h4 className="font-semibold text-indigo-900 mb-2 text-sm">Proposal Totals (All Services Combined)</h4>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                        {[
                          { label: 'Gross Revenue', value: fc(agg.gross_revenue) },
                          { label: 'Merchant Fees', value: fc(agg.merchant_fees), color: 'text-red-700' },
                          { label: 'Gross Profit', value: fc(agg.gross_profit), color: 'text-emerald-700' },
                          { label: 'GPM', value: fp(agg.gpm), color: 'text-blue-700' },
                          { label: 'Net Revenue', value: fc(agg.net_fees), color: 'text-emerald-700' },
                          { label: 'NPM', value: fp(agg.npm), color: 'text-blue-700' },
                        ].map((m, i) => (
                          <div key={i} className="text-center">
                            <p className="text-[11px] text-indigo-600 uppercase tracking-wide">{m.label}</p>
                            <p className={`text-sm font-bold ${m.color || 'text-indigo-900'}`}>{m.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (selectedProposal || selectedProposals.size > 0) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div data-testid="approval-modal" className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              {approvalAction === 'approved' ? (
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              ) : approvalAction === 'reopened' ? (
                <RotateCcw className="w-8 h-8 text-blue-500" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {approvalAction === 'approved' ? 'Approve' : approvalAction === 'reopened' ? 'Reopen' : 'Reject'} Proposal
                </h3>
                <p className="text-sm text-slate-500">
                  {selectedProposal
                    ? `${selectedProposal.collection_name} — ${selectedProposal.service_count} service${selectedProposal.service_count !== 1 ? 's' : ''}`
                    : `${selectedProposals.size} proposal${selectedProposals.size !== 1 ? 's' : ''} selected`
                  }
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Comments {approvalAction === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                data-testid="approval-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                placeholder={
                  approvalAction === 'approved' ? 'Optional comments...' : 
                  approvalAction === 'reopened' ? 'Reason for reopening (optional)...' :
                  'Reason for rejection (required)...'
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                data-testid="cancel-approval-btn"
                onClick={() => { setShowApprovalModal(false); setSelectedProposal(null); }}
                className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-approval-btn"
                onClick={submitApproval}
                disabled={processing || (approvalAction === 'rejected' && !comments.trim())}
                className={`px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 ${
                  approvalAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 
                  approvalAction === 'reopened' ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processing ? 'Processing...' : 
                 approvalAction === 'approved' ? 'Confirm Approval' : 
                 approvalAction === 'reopened' ? 'Confirm Reopen' :
                 'Confirm Rejection'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {pdfViewer.show && (
        <PdfViewerModal
          url={pdfViewer.url}
          name={pdfViewer.filename.replace('.pdf', '')}
          blob={pdfViewer.blob}
          filename={pdfViewer.filename}
          onClose={closePdfViewer}
        />
      )}

      {/* Audit Trail Modal */}
      {auditTrailModal.show && (
        <AuditTrailModal
          collectionId={auditTrailModal.collectionId}
          collectionName={auditTrailModal.collectionName}
          onClose={() => setAuditTrailModal({ show: false, collectionId: null, collectionName: '' })}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && proposalToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete Approval?</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 font-medium mb-1">{proposalToDelete.collection_name}</p>
              <p className="text-xs text-red-600">
                {proposalToDelete.service_count} service{proposalToDelete.service_count !== 1 ? 's' : ''} will be permanently deleted
              </p>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Are you sure you want to delete this approval? This will remove all associated data and cannot be recovered.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deletingId}
                className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deletingId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Summary Modal (Double-Click) */}
      {kpiModal.show && kpiModal.proposal && (() => {
        const p = kpiModal.proposal;
        const agg = p.aggregated_totals || {};
        const info = p.scenario_info || {};
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" data-testid="kpi-summary-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Proposal KPI Summary
                  </h2>
                  <p className="text-sm text-indigo-100 mt-0.5">{p.collection_name || 'Untitled Proposal'}</p>
                </div>
                <button
                  onClick={() => setKpiModal({ show: false, proposal: null })}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="close-kpi-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto flex-1 p-6">
                {/* Submission Info */}
                <div className="flex items-center gap-6 mb-6 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Submitted by <span className="font-medium text-slate-800">{p.submitter_name || p.creator_name || info.accountExecutive || 'Unknown'}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(p.submitted_at || Date.now()).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    <span><span className="font-medium text-indigo-700">{p.service_count}</span> Service{p.service_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Primary KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Gross Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{fc(agg.gross_revenue)}</p>
                    <p className="text-xs text-slate-500 mt-1">Annual Total Fees</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Net Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700">{fc(agg.net_fees)}</p>
                    <p className="text-xs text-slate-500 mt-1">After All Deductions</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Gross Margin</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">{fp(agg.gpm)}</p>
                    <p className="text-xs text-slate-500 mt-1">GPM</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Net Margin</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">{fp(agg.npm)}</p>
                    <p className="text-xs text-slate-500 mt-1">NPM</p>
                  </div>
                </div>

                {/* Secondary KPIs */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                  {[
                    { label: 'Total Transactions', value: (agg.total_transactions || 0).toLocaleString(), icon: Activity },
                    { label: 'Gross Profit', value: fc(agg.gross_profit), color: 'text-emerald-700' },
                    { label: 'Merchant Fees', value: fc(agg.merchant_fees), color: 'text-red-600' },
                    { label: 'Partnership Cost', value: fc(agg.partnership_cost || 0), color: 'text-orange-600' },
                    { label: 'Sales Commission', value: fc(agg.sales_commission || 0), color: 'text-blue-600' },
                    { label: 'Reader Costs', value: fc(agg.reader_costs || 0), color: 'text-slate-600' },
                  ].map((kpi, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{kpi.label}</p>
                      <p className={`text-sm font-bold ${kpi.color || 'text-slate-900'}`}>{kpi.value}</p>
                    </div>
                  ))}
                </div>

                {/* Service-Level Breakdown */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-600" />
                    Service-Level Profitability
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100 text-slate-700">
                          <th className="text-left px-4 py-2 font-semibold">Service</th>
                          <th className="text-right px-4 py-2 font-semibold">Transactions</th>
                          <th className="text-right px-4 py-2 font-semibold">Gross Revenue</th>
                          <th className="text-right px-4 py-2 font-semibold">Merchant Fees</th>
                          <th className="text-right px-4 py-2 font-semibold">Gross Profit</th>
                          <th className="text-right px-4 py-2 font-semibold">GPM</th>
                          <th className="text-right px-4 py-2 font-semibold">Net Revenue</th>
                          <th className="text-right px-4 py-2 font-semibold">NPM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(p.services || []).map((svc, idx) => {
                          const sCalc = svc.calculation_results?.totals || {};
                          const sProf = svc.profitability_results || {};
                          const sTotals = sProf.totals || {};
                          const sNetFees = svc.calculation_results?.net_fees || {};
                          return (
                            <tr key={svc.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-4 py-2 font-medium text-slate-800">
                                {svc.service_name || 'Service'}
                                {svc.merchant_fees_customized && (
                                  <span className="ml-1 text-[9px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded font-semibold">CUSTOM</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right text-slate-700">{(sProf.annual_transactions || 0).toLocaleString()}</td>
                              <td className="px-4 py-2 text-right text-slate-700">{fc(sCalc.total_fees)}</td>
                              <td className="px-4 py-2 text-right text-red-600">{fc(sCalc.merchant_fees)}</td>
                              <td className="px-4 py-2 text-right text-emerald-700 font-medium">{fc(sCalc.net_revenue)}</td>
                              <td className="px-4 py-2 text-right text-blue-700 font-medium">{fp(sCalc.gpm)}</td>
                              <td className="px-4 py-2 text-right text-emerald-700 font-semibold">{fc(sNetFees.net_fees)}</td>
                              <td className="px-4 py-2 text-right text-blue-700 font-semibold">{fp(sNetFees.net_fees_margin)}</td>
                            </tr>
                          );
                        })}
                        {/* Totals Row */}
                        <tr className="bg-indigo-50 font-bold border-t-2 border-indigo-200">
                          <td className="px-4 py-2 text-indigo-900">TOTALS</td>
                          <td className="px-4 py-2 text-right text-indigo-900">{(agg.total_transactions || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right text-indigo-900">{fc(agg.gross_revenue)}</td>
                          <td className="px-4 py-2 text-right text-red-700">{fc(agg.merchant_fees)}</td>
                          <td className="px-4 py-2 text-right text-emerald-700">{fc(agg.gross_profit)}</td>
                          <td className="px-4 py-2 text-right text-blue-700">{fp(agg.gpm)}</td>
                          <td className="px-4 py-2 text-right text-emerald-700">{fc(agg.net_fees)}</td>
                          <td className="px-4 py-2 text-right text-blue-700">{fp(agg.npm)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Approval Reasons (if any) */}
                {p.approval_reasons && p.approval_reasons.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-amber-900 mb-2 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Approval Required Because:
                    </h4>
                    <ul className="space-y-1">
                      {p.approval_reasons.map((reason, idx) => (
                        <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                          <span className="text-amber-600 mt-1">&bull;</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Aggregated Profitability Tables (if available) */}
                {p.aggregated_profitability && (
                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-indigo-600" />
                      Payment Type Profitability Breakdown
                    </h3>
                    <ProfitabilityTablesDetailed profitabilityResult={p.aggregated_profitability} />
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <button
                  onClick={() => navigate(`/proposal/${p.collection_id}`)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Full Proposal
                </button>
                <button
                  onClick={() => setKpiModal({ show: false, proposal: null })}
                  className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Action Bar - Manager+ only */}
      {['admin', 'manager', 'approver', 'cfo', 'ceo'].includes(user?.role) && (
        <BulkActionBar
          selectedCount={selectedProposals.size}
          onApprove={handleBulkApprove}
          onReject={handleBulkReject}
          onClear={clearSelection}
          onBulkExport={handleBulkExport}
        />
      )}
    </div>
  );
}
